import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { MaskedLines } from "../lib/reveal";
import { ArrowDownRight } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";

const HERO_IMG =
  "https://images.unsplash.com/photo-1715766911065-83723bc00d2f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwzfHxoZWFsdGh5JTIwc29pbCUyMGdyZWVuJTIwcGxhbnR8ZW58MHx8fHwxNzg1MjQwMTg0fDA&ixlib=rb-4.1.0&q=85";

export const Hero = () => {
  const ref = useRef(null);
  const { t, lang } = useLang();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  const headlineLines =
    lang === "hi"
      ? ["अपशिष्ट परितंत्र में", "जैविक सक्रियता की", "पुनर्स्थापना।"]
      : ["Restoring biological", "activity to waste", "ecosystems."];

  return (
    <section ref={ref} id="top" data-testid="hero-section" className="relative min-h-screen pt-28 md:pt-32 overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
          className="flex items-center gap-3 mb-8"
        >
          <span className="h-px w-10 bg-moss" />
          <span className="text-xs tracking-[0.22em] uppercase font-semibold text-moss">
            {t("Biological Landfill Remediation · India", "जैविक लैंडफिल उपचार · भारत")}
          </span>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8 items-end">
          <div className="lg:col-span-8">
            <h1 className="font-serif font-medium tracking-tighter leading-[0.92] text-5xl sm:text-7xl lg:text-[6.2rem] text-ink">
              <MaskedLines
                lines={headlineLines}
                delay={0.25}
              />
            </h1>
          </div>

          <div className="lg:col-span-4 lg:pb-4">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.7 }}
              className="text-base leading-relaxed text-slate max-w-sm"
            >
              {t(
                "OED — the Odour Eliminator & Decomposer — is a scalable indigenous microbial solution that eliminates landfill odour, accelerates decomposition and restores biological life to the ground.",
                "OED — गंध निवारक एवं अपघटक — एक स्केलेबल स्वदेशी सूक्ष्मजीवी समाधान है जो लैंडफिल की गंध समाप्त करता है, अपघटन को तेज़ करता है और भूमि में जैविक जीवन बहाल करता है।"
              )}
            </motion.p>
            <motion.a
              href="#contact"
              data-testid="hero-cta"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.85 }}
              className="group mt-8 inline-flex items-center gap-2 rounded-full bg-forest text-bone text-sm tracking-[0.1em] uppercase px-7 py-4 hover:bg-moss transition-colors duration-300"
            >
              {t("Request a Pilot", "पायलट का अनुरोध करें")}
              <ArrowDownRight size={18} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:translate-y-0.5" />
            </motion.a>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.95 }}
          className="relative mt-14 md:mt-20 rounded-3xl overflow-hidden aspect-[16/8] border border-stone/20"
        >
          <motion.img
            src={HERO_IMG}
            alt="Healthy soil with young plants sprouting, representing restored biological activity"
            style={{ y: imgY, scale: imgScale }}
            className="w-full h-[125%] object-cover -mt-[6%]"
          />
          <div className="absolute bottom-0 left-0 right-0 flex flex-wrap gap-8 md:gap-16 p-6 md:p-9 bg-gradient-to-t from-forest/70 to-transparent">
            {[
              ["15", t("Municipal corporations deployed", "नगर निगमों में तैनात")],
              ["50–60%", t("of India's waste is organic", "भारत का अपशिष्ट जैविक है")],
              ["4,000+", t("urban local bodies addressable", "संभावित शहरी निकाय")],
            ].map(([n, l]) => (
              <div key={l} className="text-bone">
                <div className="font-serif text-4xl md:text-5xl leading-none">{n}</div>
                <div className="text-[0.72rem] md:text-xs tracking-[0.12em] uppercase mt-2 text-bone/80 max-w-[9rem]">{l}</div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};
